'use client';

// Root error boundary — reports client_error + PostHog exception capture.

import { Component, type ErrorInfo, type ReactNode } from 'react';
import Link from 'next/link';
import {
  EVENTS,
  captureException,
  safeErrorMessage,
  track,
} from '@/lib/analytics';

import Button, { buttonClass } from '@/components/ui/Button';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const message = safeErrorMessage(error);
    track(EVENTS.client_error, {
      message,
      source: 'error_boundary',
      component_stack: info.componentStack?.slice(0, 200),
    });
    captureException(error, { source: 'error_boundary' });
  }

  private retry = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-[var(--app-bg)] px-6 text-center text-[var(--rt-ink)]">
          <div className="max-w-sm space-y-2">
            <h1 className="text-[18px] font-semibold tracking-tight">Something went wrong</h1>
            <p className="text-[13px] leading-relaxed text-[var(--rt-ink-dim)]">
              An unexpected error stopped this page. You can try again or return home.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="secondary" size="md" onClick={this.retry}>
              Try again
            </Button>
            <Link
              href="/"
              className={buttonClass({ variant: 'ghost', size: 'md' })}
            >
              Back to home
            </Link>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
